import { combineLatest, of, startWith } from 'rxjs'
import { map } from 'rxjs/operators'
import dayjs from 'dayjs'
import produce from 'immer'
import { result } from 'libraries/lodash'
import debug from 'libraries/front-end-core/core/debug'
import { RESPONSE_BADDATA, RESPONSE_PENDING } from 'libraries/front-end-core/constants/responses'
import { mapResponse, mergeResponses } from 'libraries/front-end-core/helpers/dataflow'
import {
  QA_SEARCH,
  QA_SEARCH_PREPAY,
  QA_SECTIONS,
  QA_SECTIONS_PREPAY,
  LEGISLATION_SEARCH,
  LEGISLATION_SEARCH_PREPAY,
  CURRENT_AWARENESS_SEARCH,
  CURRENT_AWARENESS_SEARCH_PREPAY,
  DOCUMENTS_SEARCH,
  DOCUMENT_SECTIONS,
  DOCUMENTS_SEARCH_PREPAY
} from 'constants/requests'
import { DEFAULT_FLAVOUR } from 'constants/flavours'
import { fetch } from 'services/ApiService'
import loggedRequestSwitcher from 'helpers/loggedRequestSwitcher'
import { mapDate } from './CurrentAwarenessService'
import { DATE_FORMAT, TIME_FORMAT } from 'constants/locale'
import observeRequest from 'helpers/fcObserveRequest'
import { URL_VECTOR_SEARCH } from 'constants/urls'
import { toHtml } from 'libraries/front-end-core/helpers/xml'
import { ALL_AOLS } from 'constants/aol/aol'

export const addPrecedentPaths = function (search, sections) {
  const getTitle = (id) => result(sections, ['nodes', id, 'title'], null)
  const results = search.results.map((p) => ({
    ...p,
    parentTitle: getTitle(p.parentId),
    sectionId: result(sections, ['nodes', p.parentId, 'path', 0], null),
    sectionTitle: getTitle(result(sections, ['nodes', p.parentId, 'path', 0]))
  }))

  return { ...search, results }
}

const addQaPaths = function (search, sections) {
  try {
    const getPathElement = (id) => ({ id, title: sections.nodes[id].title })
    const getPath = (id) => sections.nodes[id].path.map(getPathElement)
    const results = search.results.reduce((acc, curr) => {
      try {
        const path = getPath(curr.sectionId)
        return [...acc, { ...curr, path }]
      } catch (error) {
        return acc
      }
    }, [])

    return { ...search, results }
  } catch (e) {
    debug.error("Search results parent ids don't match section ids.")
    return RESPONSE_BADDATA
  }
}

const requestQASearchResults = (flavour, queryString, init, limit, params = {}) => {
  const searchBody = { q: queryString, i: init, s: limit, ...params }

  const loggedRequest = () => combineLatest(
    [fetch(QA_SEARCH, { flavour }, searchBody),
      // Using the prepay endpoint for sections here because we will return the questions only for logged in
      // users without the relevant roles and the secure endpoint will filter sections based on roles so we need
      // to make sure get all sections back to be able to map paths correctly
      fetch(QA_SECTIONS_PREPAY, { flavour, ...params })],
    mapResponse(addQaPaths)
  )
  const prepayRequest = () => combineLatest(
    [fetch(QA_SEARCH_PREPAY, { flavour }, searchBody),
      fetch(QA_SECTIONS_PREPAY, { flavour, ...params })],
    mapResponse(addQaPaths)
  )
  return loggedRequestSwitcher(loggedRequest, prepayRequest)
}

const requestLegislationSearchResults = ({ query }) => {
  const loggedRequest = () => fetch(LEGISLATION_SEARCH, {}, query)
  const prepayRequest = () => fetch(LEGISLATION_SEARCH_PREPAY, {}, query)
  return loggedRequestSwitcher(loggedRequest, prepayRequest)
}

const requestPrecedentsSearchResults = ({ params = {}, searchQuery }) => {
  const aol = 'corporate'
  const loggedRequest = () =>
    combineLatest(
      [
        fetch(DOCUMENTS_SEARCH, { ...params, aol }, searchQuery),
        fetch(DOCUMENT_SECTIONS, { ...params, aol, flavour: DEFAULT_FLAVOUR })
      ],
      mapResponse(addPrecedentPaths)
    )
  const prepayRequest = () =>
    combineLatest(
      [
        fetch(DOCUMENTS_SEARCH_PREPAY, { ...params, aol }, searchQuery),
        fetch(DOCUMENT_SECTIONS, {
          flavour: DEFAULT_FLAVOUR,
          aol
        })
      ],
      mapResponse(addPrecedentPaths)
    )
  return loggedRequestSwitcher(loggedRequest, prepayRequest)
}

const mapPublicationDates = (resp) => {
  return produce(resp, (draft) => {
    draft.results = draft.results.map((result) => {
      return produce(result, (draft) => {
        draft.dateOfPublication = mapDate(draft.dateOfPublication)
      })
    })
  })
}

const requestCurrentAwarenessSearchResults = ({
  params,
  fromDate,
  toDate,
  aol
}) => {
  const urlVars = {
    fromDate: fromDate && toDate ? fromDate : null,
    toDate: fromDate && toDate ? toDate : null,
    search: fromDate && toDate ? null : 'search',
    aol
  }
  const loggedRequest = () => fetch(CURRENT_AWARENESS_SEARCH, urlVars, params)
  const prepayRequest = () =>
    fetch(CURRENT_AWARENESS_SEARCH_PREPAY, urlVars, params)
  return loggedRequestSwitcher(loggedRequest, prepayRequest).pipe(
    map(mapResponse(mapPublicationDates))
  )
}

export const requestSmartSearchResults = ({
  queryString,
  smartSearchEnabled,
  selectedAols,
  caseFlag,
  flavour
}) => {
  if (queryString && smartSearchEnabled) {
    return observeRequest({
      url: URL_VECTOR_SEARCH,
      headers: { 'Content-Type': 'application/json' },
      method: 'post',
      body: JSON.stringify({
        question: queryString,
        aols: selectedAols?.length ? selectedAols : ALL_AOLS,
        caseFlag,
        flavour
      })
    }).pipe(
      map(mapResponse(resp => {
        let parsed = resp
        if (typeof resp === 'string') {
          try {
            parsed = JSON.parse(resp)
          } catch (e) {
            console.warn('Failed to parse smartsearch API response as JSON:', resp)
            parsed = {}
          }
        }
        const foundQa = parsed.foundQa.map(({ answerContent, questionContent, ...rest }) => ({
          answerContent: toHtml(answerContent),
          questionContent: toHtml(questionContent),
          ...rest
        }))
        const verifiedAnswer = parsed.verifiedQa
        if (verifiedAnswer) {
          verifiedAnswer.answerContent = toHtml(verifiedAnswer.answerContent)
          verifiedAnswer.questionContent = toHtml(verifiedAnswer.questionContent)
        }

        const lowScoreAlert = parsed.lowScoreAlert ?? false
        const lowResultAlert = parsed.lowNumResultsAlert ?? false

        return { foundQa, verifiedAnswer, lowScoreAlert, lowResultAlert }
      }))
    )
  }
  return of({ foundQa: [] })
}

export const getSmartSearchResults = ({
  queryString,
  smartSearchEnabled,
  selectedAols,
  caseFlag,
  flavour
}) => {
  const results$ = requestSmartSearchResults({
    queryString,
    smartSearchEnabled,
    selectedAols,
    caseFlag,
    flavour
  })

  const sections$ = fetch(QA_SECTIONS, { flavour })

  return combineLatest([results$, sections$], mergeResponses).pipe(
    map(mapResponse((results) => {
      const foundQa = Array.isArray(results?.foundQa) ? results.foundQa : []
      const verifiedAnswer = results?.verifiedAnswer
      const nodes = (!Array.isArray(results.nodes) && results.nodes) || {}
      const getPathElement = id => ({ id, title: nodes[id]?.title })
      const getPath = item => {
        const parent = nodes[item.parentSectionBookmarkId]
        if (parent && Array.isArray(parent.path)) {
          return [
            ...parent.path.map(getPathElement),
            {
              id: item.bookmarkId,
              title: item.questionContent?.replace(/<[^>]+>/g, '').trim() || 'Q&A'
            }
          ]
        }
        return [
          {
            id: item.bookmarkId || 'unknown',
            title: item.questionContent?.replace(/<[^>]+>/g, '').trim() || 'Section'
          }
        ]
      }

      if (verifiedAnswer) {
        verifiedAnswer.path = getPath(verifiedAnswer)
      }

      return {
        ...results,
        foundQa: foundQa.map(item => ({ ...item, path: getPath(item) })),
        sections: { nodes },
        verifiedAnswer
      }
    }))
  )
}

export const requestSearchResults = ({
  searchPhrase: queryString,
  searchType,
  pageNumber,
  pageLimit,
  flavour,
  query
}) => {
  const limit = +pageLimit
  const init = ((+pageNumber || 1) - 1) * limit + 1

  switch (searchType) {
    case 'qa': {
      const qaResults$ = requestQASearchResults(
        flavour,
        queryString,
        init,
        limit,
        query
      ).pipe(startWith(RESPONSE_PENDING))

      const sections$ = loggedRequestSwitcher(
        () => fetch(QA_SECTIONS, { flavour }),
        () => fetch(QA_SECTIONS_PREPAY, { flavour })
      ).pipe(startWith(RESPONSE_PENDING))

      return combineLatest([qaResults$, sections$]).pipe(
        map(([qaResults, sections]) =>
          (qaResults === RESPONSE_PENDING || sections === RESPONSE_PENDING)
            ? RESPONSE_PENDING
            : addQaPaths({ ...qaResults, sections: sections || [] }, sections || [])
        )
      )
    }
    case 'legislation':
      return requestLegislationSearchResults({
        query: { q: queryString, i: init, s: limit, ...query }
      })
    case 'documents':
      return requestPrecedentsSearchResults({
        searchQuery: {
          search: queryString,
          limit,
          init: init - 1,
          query: query.library && `library=${query.library}`,
          automated: query.automated
        }
      })
    case 'ca':
      return requestCurrentAwarenessSearchResults({
        params: {
          page: pageNumber || 1,
          size: pageLimit,
          format: 'slim',
          q: queryString,
          chronological: query.sort !== 'Relevance',
          aol: query.aol
        },
        fromDate: query.fromDate
          ? dayjs(query.fromDate).format(`${DATE_FORMAT}T${TIME_FORMAT}`)
          : null,
        toDate: query.toDate
          ? dayjs(query.toDate).format(`${DATE_FORMAT}T${TIME_FORMAT}`)
          : null
      })
    default:
      return of(RESPONSE_BADDATA)
  }
}
